import { useEffect, useId, useLayoutEffect, useRef, useState, type CSSProperties, type KeyboardEvent } from 'react'
import { Check, ChevronDown } from 'lucide-react'
import clsx from 'clsx'

export interface SelectOption {
  value: string
  label: string
}

export interface SelectProps {
  id?: string
  value: string
  onChange: (value: string) => void
  options: SelectOption[]
  disabled?: boolean
  className?: string
}

const VIEWPORT_MARGIN = 8

// Fixed (out-of-flow) and hidden even for the one frame before the layout
// effect below measures the button - same fix as Tooltip.tsx's own
// HIDDEN_STYLE: without this, the list briefly renders in normal flow (no
// `position` set yet), pushing whatever follows the Select down the page for
// a frame, most visible when Select opens inside a Modal.
const HIDDEN_STYLE: CSSProperties = { position: 'fixed', top: 0, left: 0, visibility: 'hidden' }

// Custom listbox, not a native <select> - a native control's own open list is
// unstyleable OS chrome (a system blue highlight bar, a system checkmark, a
// mismatched font/corner-radius), the actual source of the "abrupt"/
// inconsistent look next to the rest of this app's own dark theme. Every
// screen's own dropdown (firm picker, field type, lookup type, add-to-group)
// renders through this one component now, so the look stays uniform.
//
// Positioned via JS-measured `position: fixed` coordinates - same technique
// as Tooltip.tsx (escapes any ancestor's overflow-x-auto/overflow-hidden
// clipping, e.g. inside a Modal), not CSS `absolute`.
export function Select({ id, value, onChange, options, disabled, className }: SelectProps) {
  const [open, setOpen] = useState(false)
  const [style, setStyle] = useState<CSSProperties>(HIDDEN_STYLE)
  const [highlightedIndex, setHighlightedIndex] = useState(0)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const listRef = useRef<HTMLUListElement>(null)
  const listboxId = useId()

  const selectedIndex = options.findIndex((option) => option.value === value)
  const selectedLabel = options[selectedIndex]?.label ?? ''

  useLayoutEffect(() => {
    if (!open) {
      setStyle(HIDDEN_STYLE)
      return
    }
    const button = buttonRef.current
    if (!button) return
    const rect = button.getBoundingClientRect()
    const maxLeft = window.innerWidth - rect.width - VIEWPORT_MARGIN
    setStyle({
      position: 'fixed',
      top: rect.bottom + 4,
      left: Math.min(rect.left, Math.max(maxLeft, VIEWPORT_MARGIN)),
      width: rect.width,
      visibility: 'visible',
    })
    setHighlightedIndex(selectedIndex >= 0 ? selectedIndex : 0)
  }, [open, selectedIndex])

  useEffect(() => {
    if (!open) return
    function handlePointerDown(event: MouseEvent) {
      const target = event.target as Node
      if (buttonRef.current?.contains(target) || listRef.current?.contains(target)) return
      setOpen(false)
    }
    document.addEventListener('mousedown', handlePointerDown)
    return () => document.removeEventListener('mousedown', handlePointerDown)
  }, [open])

  function selectOption(option: SelectOption) {
    onChange(option.value)
    setOpen(false)
    buttonRef.current?.focus()
  }

  function handleKeyDown(event: KeyboardEvent<HTMLButtonElement>) {
    if (!open) {
      if (['Enter', ' ', 'ArrowDown', 'ArrowUp'].includes(event.key)) {
        event.preventDefault()
        setOpen(true)
      }
      return
    }
    if (event.key === 'Escape') {
      event.preventDefault()
      setOpen(false)
      return
    }
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      setHighlightedIndex((current) => Math.min(current + 1, options.length - 1))
      return
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault()
      setHighlightedIndex((current) => Math.max(current - 1, 0))
      return
    }
    if (event.key === 'Enter') {
      event.preventDefault()
      const option = options[highlightedIndex]
      if (option) selectOption(option)
    }
  }

  return (
    <div className={clsx('relative', className)}>
      <button
        ref={buttonRef}
        id={id}
        type="button"
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listboxId}
        disabled={disabled}
        onClick={() => setOpen((current) => !current)}
        onKeyDown={handleKeyDown}
        className="flex w-full items-center justify-between gap-2 rounded-control border border-border bg-bg px-3 py-2 text-left text-body text-text-primary outline-none disabled:cursor-not-allowed disabled:text-text-disabled"
      >
        <span className="truncate">{selectedLabel}</span>
        <ChevronDown size={16} strokeWidth={1.5} className="shrink-0 text-text-secondary" />
      </button>
      {open && (
        <ul
          ref={listRef}
          id={listboxId}
          role="listbox"
          style={style}
          className="z-50 max-h-64 overflow-y-auto rounded-control border border-border bg-surface-raised py-1 shadow-lg"
        >
          {options.map((option, index) => (
            <li
              key={option.value}
              role="option"
              aria-selected={option.value === value}
              onMouseEnter={() => setHighlightedIndex(index)}
              onClick={() => selectOption(option)}
              className={clsx(
                'flex cursor-pointer items-center gap-2 px-3 py-2 text-body',
                index === highlightedIndex && 'bg-surface',
                option.value === value ? 'text-text-primary' : 'text-text-secondary',
              )}
            >
              <Check
                size={14}
                strokeWidth={2}
                className={clsx('shrink-0', option.value === value ? 'text-accent' : 'invisible')}
              />
              <span className="truncate">{option.label}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
